import React from 'react';
import { motion } from 'framer-motion';
import { 
  ShieldCheckIcon,
  LockClosedIcon,
  EyeIcon,
  ServerIcon,
  CogIcon,
  UserGroupIcon,
  PhoneIcon
} from '@heroicons/react/24/outline';

const Privacy: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-airmax-blue to-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Privacy Policy
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto">
              Your privacy is important to us. Learn how we collect, use, and protect your information.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Last Updated */}
      <section className="py-8 bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-sm text-gray-600 text-center">
            <strong>Last Updated:</strong> March 2026
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-gray-700 leading-relaxed">
              <strong>Airmax Internet L.L.C. ("Airmax", "we", "our", or "us")</strong> respects the privacy of our customers, visitors, and users of our services. This Privacy Policy explains how we collect, use, protect, and manage personal information when you interact with our websites, services, advertisements, and communication systems.
            </p>
            <p className="text-gray-700 leading-relaxed mt-4">
              By using our website, submitting information through our forms, or using our internet services, you agree to the practices described in this Privacy Policy.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          
          {/* 1. Information We Collect */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-2xl shadow-lg p-8"
          >
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-airmax-blue rounded-full flex items-center justify-center mr-4">
                <UserGroupIcon className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">1. INFORMATION WE COLLECT</h2>
            </div>
            
            <p className="text-gray-700 mb-6">
              Airmax Internet may collect the following types of information when you interact with our services:
            </p>

            <div className="bg-blue-50 rounded-xl p-6 mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">PERSONAL INFORMATION</h3>
              <p className="text-gray-700 mb-4">This may include:</p>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>Name</li>
                <li>Phone number</li>
                <li>Email address</li>
                <li>Service address</li>
                <li>Billing address</li>
                <li>Account information</li>
                <li>Payment information (processed through secure third-party payment providers)</li>
              </ul>
              <p className="text-gray-700 mt-4">This information is typically collected when you:</p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 mt-2">
                <li>Submit a service availability request</li>
                <li>Sign up for internet services</li>
                <li>Contact customer support</li>
                <li>Submit forms through advertisements</li>
                <li>Communicate with our company</li>
              </ul>
            </div>

            <div className="bg-green-50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">TECHNICAL INFORMATION</h3>
              <p className="text-gray-700 mb-4">When visiting our website or using our services, we may also collect technical data including:</p>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>IP address</li>
                <li>Device type</li>
                <li>Browser type</li>
                <li>Operating system</li>
                <li>Website usage data</li>
                <li>Network diagnostic data necessary to provide service</li>
              </ul>
              <p className="text-gray-700 mt-4">
                This information helps us maintain network security, improve performance, and diagnose service issues.
              </p>
            </div>
          </motion.div>

          {/* 2. How We Use Your Information */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="bg-white rounded-2xl shadow-lg p-8"
          >
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-airmax-blue rounded-full flex items-center justify-center mr-4">
                <EyeIcon className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">2. HOW WE USE YOUR INFORMATION</h2>
            </div>
            
            <p className="text-gray-700 mb-6">
              Airmax Internet uses collected information only for legitimate business purposes including:
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                "Checking service availability",
                "Providing internet services",
                "Account creation and management",
                "Customer support and troubleshooting",
                "Service notifications and updates",
                "Billing and payment processing",
                "Fraud prevention and network security",
                "Improving our services and website functionality"
              ].map((item, index) => (
                <div key={index} className="flex items-start">
                  <div className="w-2 h-2 bg-airmax-blue rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <span className="text-gray-700">{item}</span>
                </div>
              ))}
            </div>

            <div className="bg-yellow-50 rounded-xl p-6 mt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Service Updates</h3>
              <p className="text-gray-700 mb-4">
                We may also use your contact information to provide updates about:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>Installation scheduling</li>
                <li>Service outages</li>
                <li>Maintenance notifications</li>
                <li>Customer support communications</li>
              </ul>
            </div>
          </motion.div>

          {/* 3. Information Sharing */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white rounded-2xl shadow-lg p-8"
          >
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-airmax-blue rounded-full flex items-center justify-center mr-4">
                <ServerIcon className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">3. INFORMATION SHARING</h2>
            </div>
            
            <div className="bg-red-50 rounded-xl p-6 mb-6">
              <p className="text-red-800 font-semibold text-lg mb-2">
                Airmax Internet does not sell, rent, or trade personal information to third parties.
              </p>
            </div>

            <p className="text-gray-700 mb-6">
              Information may only be shared in the following circumstances:
            </p>

            <div className="bg-blue-50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">SERVICE PROVIDERS</h3>
              <p className="text-gray-700 mb-4">
                We may share limited information with trusted third-party providers necessary to operate our services, such as:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>Payment processors</li>
                <li>Billing systems</li>
                <li>Customer support platforms</li>
                <li>Network monitoring systems</li>
              </ul>
              <p className="text-gray-700 mt-4">
                These providers are required to maintain strict confidentiality and may only use the information to perform services on our behalf.
              </p>
            </div>

            <div className="bg-orange-50 rounded-xl p-6 mt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">LEGAL REQUIREMENTS</h3>
              <p className="text-gray-700 mb-4">
                Airmax Internet may disclose information if required to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>Comply with applicable laws or regulations</li>
                <li>Respond to valid legal requests such as subpoenas or court orders</li>
                <li>Protect the rights, property, or safety of Airmax Internet, our customers, or the public</li>
              </ul>
            </div>
          </motion.div>

          {/* 4. Network Privacy and Monitoring */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="bg-white rounded-2xl shadow-lg p-8"
          >
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-airmax-blue rounded-full flex items-center justify-center mr-4">
                <ShieldCheckIcon className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">4. NETWORK PRIVACY AND MONITORING</h2>
            </div>
            
            <p className="text-gray-700 mb-6">
              As an Internet Service Provider, Airmax Internet may collect limited network information necessary to operate and secure our network infrastructure. This may include:
            </p>

            <ul className="list-disc list-inside space-y-2 text-gray-700 mb-6">
              <li>Traffic usage statistics</li>
              <li>Network diagnostic logs</li>
              <li>Security monitoring information</li>
            </ul>

            <div className="bg-yellow-50 rounded-xl p-6">
              <p className="text-gray-700">
                Airmax Internet does not monitor or inspect the content of customer communications except where necessary to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 mt-2">
                <li>Maintain network performance</li>
                <li>Investigate abuse or security incidents</li>
                <li>Comply with legal obligations</li>
              </ul>
            </div>
          </motion.div>

          {/* 5. Data Security */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="bg-white rounded-2xl shadow-lg p-8"
          >
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-airmax-blue rounded-full flex items-center justify-center mr-4">
                <LockClosedIcon className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">5. DATA SECURITY</h2>
            </div>
            
            <p className="text-gray-700 mb-6">
              Airmax Internet takes reasonable administrative, technical, and physical measures to protect personal information from unauthorized access, disclosure, or misuse. These protections may include:
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {[
                "Secure servers",
                "Network firewalls",
                "Access control systems",
                "Encrypted communications where applicable"
              ].map((item, index) => (
                <div key={index} className="flex items-center bg-green-50 rounded-lg p-3">
                  <div className="w-2 h-2 bg-green-600 rounded-full mr-3"></div>
                  <span className="text-gray-700">{item}</span>
                </div>
              ))}
            </div>

            <div className="bg-yellow-50 rounded-xl p-6">
              <p className="text-gray-700">
                However, no system can guarantee absolute security, and users should take precautions to protect their own information as well.
              </p>
            </div>
          </motion.div>

          {/* 6. Cookies and Website Tracking */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="bg-white rounded-2xl shadow-lg p-8"
          >
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-airmax-blue rounded-full flex items-center justify-center mr-4">
                <CogIcon className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">6. COOKIES AND WEBSITE TRACKING</h2>
            </div>
            
            <p className="text-gray-700 mb-6">
              Our website may use cookies or similar technologies to:
            </p>

            <ul className="list-disc list-inside space-y-2 text-gray-700 mb-6">
              <li>Improve website performance</li>
              <li>Analyze website usage</li>
              <li>Remember user preferences</li>
            </ul>

            <div className="bg-blue-50 rounded-xl p-6">
              <p className="text-gray-700">
                Users may disable cookies through their browser settings, though some website features may not function properly.
              </p>
            </div>
          </motion.div>

          {/* 7. Third-Party Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="bg-white rounded-2xl shadow-lg p-8"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-6">7. THIRD-PARTY LINKS</h2>
            
            <p className="text-gray-700 mb-4">
              Our website or advertisements may contain links to third-party websites. Airmax Internet is not responsible for the privacy practices or content of external websites.
            </p>

            <div className="bg-yellow-50 rounded-xl p-6">
              <p className="text-gray-700">
                Users should review the privacy policies of any third-party sites they visit.
              </p>
            </div>
          </motion.div>

          {/* 8. Data Retention */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="bg-white rounded-2xl shadow-lg p-8"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-6">8. DATA RETENTION</h2>
            
            <p className="text-gray-700 mb-6">
              Airmax Internet retains personal information only as long as necessary to:
            </p>

            <ul className="list-disc list-inside space-y-2 text-gray-700">
              <li>Provide services</li>
              <li>Maintain customer accounts</li>
              <li>Comply with legal and regulatory requirements</li>
              <li>Resolve disputes or enforce agreements</li>
            </ul>
          </motion.div>

          {/* 9. Your Privacy Rights */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="bg-white rounded-2xl shadow-lg p-8"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-6">9. YOUR PRIVACY RIGHTS</h2>
            
            <p className="text-gray-700 mb-6">
              Customers may request to:
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                "Review their personal information",
                "Update incorrect information",
                "Request account information changes"
              ].map((item, index) => (
                <div key={index} className="bg-blue-50 rounded-lg p-4 text-center">
                  <div className="w-8 h-8 bg-airmax-blue rounded-full flex items-center justify-center mx-auto mb-2">
                    <span className="text-white font-bold">{index + 1}</span>
                  </div>
                  <p className="text-gray-700 text-sm">{item}</p>
                </div>
              ))}
            </div>

            <div className="bg-green-50 rounded-xl p-6 mt-6">
              <p className="text-gray-700">
                Requests may be submitted by contacting our support team.
              </p>
            </div>
          </motion.div>

          {/* 10. Children's Privacy */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.9 }}
            className="bg-white rounded-2xl shadow-lg p-8"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-6">10. CHILDREN'S PRIVACY</h2>
            
            <div className="bg-blue-50 rounded-xl p-6">
              <p className="text-gray-700">
                Airmax Internet services are not directed to individuals under the age of 13. We do not knowingly collect personal information from children without parental consent.
              </p>
            </div>
          </motion.div>

          {/* 11. Policy Updates */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.0 }}
            className="bg-white rounded-2xl shadow-lg p-8"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-6">11. POLICY UPDATES</h2>
            
            <p className="text-gray-700 mb-6">
              Airmax Internet may update this Privacy Policy periodically to reflect changes in services, legal requirements, or operational practices.
            </p>

            <div className="bg-yellow-50 rounded-xl p-6">
              <p className="text-gray-700">
                Updates will be posted on our website with the revised effective date.
              </p>
            </div>
          </motion.div>

          {/* 12. SMS Communications */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.1 }}
            className="bg-white rounded-2xl shadow-lg p-8"
          >
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-airmax-blue rounded-full flex items-center justify-center mr-4">
                <PhoneIcon className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">12. SMS COMMUNICATIONS</h2>
            </div>
            
            <p className="text-gray-700 mb-6">
              If you provide your mobile phone number and consent to receive SMS messages from Airmax Internet, we may send text messages related to your account and the services we provide. These messages may include:
            </p>

            <div className="bg-blue-50 rounded-xl p-6 mb-6">
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>Service appointment reminders</li>
                <li>Installation scheduling</li>
                <li>Customer support communications</li>
                <li>Billing notifications and payment reminders</li>
                <li>Service outage and maintenance notifications</li>
                <li>Account-related updates</li>
                <li>Promotional or marketing messages (only where you have provided consent)</li>
              </ul>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="bg-yellow-50 rounded-lg p-4">
                <p className="text-gray-700 font-semibold">Message Frequency</p>
                <p className="text-gray-700 text-sm mt-2">Message frequency may vary depending on your account activity and the services you request.</p>
              </div>
              <div className="bg-orange-50 rounded-lg p-4">
                <p className="text-gray-700 font-semibold">Message and Data Rates</p>
                <p className="text-gray-700 text-sm mt-2">Message and data rates may apply based on your mobile carrier's plan.</p>
              </div>
            </div>

            <div className="bg-green-50 rounded-xl p-6 mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Opt-Out Information</h3>
              <p className="text-gray-700 mb-4">
                You may opt out of receiving SMS messages at any time by replying <strong>STOP</strong> to any message. For assistance, reply <strong>HELP</strong> or contact Airmax Internet customer support.
              </p>
            </div>

            <div className="bg-red-50 rounded-xl p-6">
              <p className="text-gray-700 font-semibold">
                SMS consent is not shared with third parties or affiliates for marketing purposes.
              </p>
            </div>
          </motion.div>

        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Questions About Your Privacy?</h2>
            <p className="text-xl text-gray-600 mb-8">
              If you have any questions about this Privacy Policy or how we handle your information, please contact us.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="tel:928-565-6707" 
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                Call 928-565-6707
              </a>
              <a 
                href="mailto:airmaxhelpdesk@gmail.com" 
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                Email Us
              </a>
            </div>
            <p className="text-sm text-gray-500 mt-6">
              Phone Support: Mon–Fri 9 AM–5 PM (Arizona Time) · Online Support Center: Available 24/7
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Privacy;
